import React, { useState } from "react";
import "./login.css";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import login_img from "../../assets/image/password_change.jpeg";
import { api } from "../../api/api";
import { jwtDecode } from "jwt-decode";
import {
  OtpPanel,
  emptyOtp,
  getApiMessage,
  isApiSuccess,
  prettyPhone,
  useCountdown,
} from "../../Components/common/OtpInput/OtpInput";

const OTP_VALIDITY = 15 * 60; // backend keeps password-reset OTPs for 15 minutes
const RESEND_COOLDOWN = 30;

// Same rule as the backend's isStrongPassword()
const STRONG_PASSWORD = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const phone = queryParams.get("phone") || "";
  const token = queryParams.get("token");
  let decodedEmail = "";
  try {
    if (token) decodedEmail = jwtDecode(token).email || "";
  } catch (error) {
    console.error("Invalid token", error);
  }
  const email = queryParams.get("email") || decodedEmail;

  const identifier = phone ? { phone } : email ? { email } : null;
  const target = phone ? prettyPhone(phone) : email;

  const [otp, setOtp] = useState(emptyOtp());
  const [otpError, setOtpError] = useState("");
  const [formError, setFormError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [expiresIn, setExpiresIn] = useCountdown(OTP_VALIDITY);
  const [resendIn, setResendIn] = useCountdown(RESEND_COOLDOWN);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: { password: "", confirmPassword: "" } });
  const password = watch("password");

  // Reset OTPs are issued by forgotPassword (resendOTP refuses verified accounts)
  const handleResendOTP = async () => {
    if (!identifier) return;
    setOtpError("");
    try {
      setResending(true);
      const res = await api("api/v1/student/forgotPassword", identifier, "postWithoutToken");
      if (!isApiSuccess(res)) throw new Error(getApiMessage(res, "Failed to resend OTP"));
      setOtp(emptyOtp());
      setExpiresIn(OTP_VALIDITY);
      setResendIn(RESEND_COOLDOWN);
    } catch (e) {
      setOtpError(e?.message || "Something went wrong while sending OTP");
    } finally {
      setResending(false);
    }
  };

  const onSubmit = async (data) => {
    setOtpError("");
    setFormError("");
    const code = otp.join("");
    if (code.length !== otp.length) {
      setOtpError("Please enter the complete 6-digit code.");
      return;
    }
    if (expiresIn <= 0) {
      setOtpError("This code has expired. Please request a new one.");
      return;
    }
    try {
      setSubmitting(true);
      const res = await api(
        "api/v1/student/resetPassword",
        { ...identifier, otp: code, newPassword: data.password },
        "postWithoutToken"
      );
      if (!isApiSuccess(res)) throw new Error(getApiMessage(res, "Password reset failed"));
      navigate("/login");
    } catch (e) {
      setFormError(e?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="register-area login-area overflow-hidden">
      <div className="container">
        <div className="row">
          <div className="col-lg-6 mx-auto">
            <h3 className="text-center mb-4">
              Reset Password
              <span>Verify the code and choose a new password.</span>
            </h3>
            <div className="img_sec img_showcase">
              <img src={login_img} alt="Reset password" />
              <span className="img_chip chip_top" aria-hidden="true">
                <i className="fas fa-sms" /> OTP verification
              </span>
              <span className="img_chip chip_bottom" aria-hidden="true">
                <i className="fas fa-key" /> New password
              </span>
            </div>

            <div className="contact-form">
              {identifier ? (
                <form className="mt-4" onSubmit={handleSubmit(onSubmit)} noValidate>
                  <div className="row">
                    <div className="col-md-10 mx-auto">
                      <OtpPanel
                        target={target}
                        otp={otp}
                        setOtp={setOtp}
                        error={otpError}
                        expiresIn={expiresIn}
                        resendIn={resendIn}
                        onResend={handleResendOTP}
                        resending={resending}
                      />

                      <div className="mb-3 px-2 mt-5">
                        <div className="password">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="form-control"
                            placeholder="*New Password"
                            autoComplete="new-password"
                            {...register("password", {
                              required: "Password is required",
                              pattern: {
                                value: STRONG_PASSWORD,
                                message:
                                  "Use 8+ characters with uppercase, lowercase, a number and a symbol",
                              },
                            })}
                          />
                          <span className="icon">
                            <i
                              className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                              onClick={() => setShowPassword((s) => !s)}
                              role="button"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              tabIndex={0}
                            ></i>
                          </span>
                        </div>
                        {errors.password ? (
                          <p className="text-danger small mb-0">{errors.password.message}</p>
                        ) : (
                          <p className="small text-muted mb-0 mt-1">
                            8+ characters with uppercase, lowercase, a number and a symbol
                          </p>
                        )}
                      </div>

                      <div className="mb-3 px-2">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          placeholder="*Confirm New Password"
                          autoComplete="new-password"
                          {...register("confirmPassword", {
                            required: "Please confirm your password",
                            validate: (value) => value === password || "Passwords do not match",
                          })}
                        />
                        {errors.confirmPassword && (
                          <p className="text-danger small mb-0">{errors.confirmPassword.message}</p>
                        )}
                      </div>

                      {formError && (
                        <div className="alert alert-danger mx-2" role="alert" style={{ fontSize: 14 }}>
                          {formError}
                        </div>
                      )}

                      <div className="px-2">
                        <button
                          className="btn_secondary btn_md w-100 mt-3 text-center"
                          type="submit"
                          disabled={submitting}
                        >
                          {submitting && (
                            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                          )}
                          {submitting ? "Resetting..." : "Reset Password"}
                        </button>
                      </div>

                      <p className="already">
                        Remember your password?
                        <Link to="/login" className="ms-2">
                          Login
                        </Link>
                      </p>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="text-center mt-4">
                  <p>This reset link is invalid or has expired.</p>
                  <Link to="/forgot-password" className="btn_secondary btn_md d-inline-flex mt-2">
                    Request a new code
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
